import {
  BOT_CATALOG,
  botById,
  rateLimitTarget,
  type AddressLookupRow,
  type BotCatalogEntry,
  type TrafficTopDimension,
  type TrafficTopRow,
  type TrafficVerification,
  type WebCapabilities,
  type WebRules,
} from '@bartizo/shared';
import type { ReactNode } from 'react';
import { botLabel, CATEGORY_LABELS } from '../lib/traffic.js';
import { Button } from '../ui/Button.js';
import { AddressLink, AddressStatusBadge } from './AddressStatus.js';
import type { BotRuleAction } from './BotRuleDialog.js';
import { Flag } from './Flag.js';

/**
 * The pieces every Traffic list shares: a row's name with what is known about it,
 * and the buttons its server can honour (P6.6; P11.1; P11.12).
 */

/** What a row may offer, for the one server chosen. */
export interface Actor {
  capabilities: WebCapabilities | undefined;
  effective: WebRules;
  onBot: (bot: BotCatalogEntry, action: BotRuleAction) => void;
  onBan: (ip: string) => void;
}

const VERIFICATION_BADGES: Readonly<
  Record<TrafficVerification, { className: string; text: string; title: string }>
> = {
  verified: {
    className: 'badge badge-success',
    text: '✔ verified',
    title: 'Every address was checked against its operator and passed',
  },
  failed: {
    className: 'badge badge-danger',
    text: '⚠ fake',
    title: 'The address is not its operator’s',
  },
  unknown: {
    className: 'badge badge-warning',
    text: 'unchecked',
    title: 'A check could not finish, or the catalog entry is not reviewed yet',
  },
  pending: { className: 'badge badge-muted', text: 'checking', title: 'Not checked yet' },
};

export function VerificationBadge({
  verification,
}: {
  verification: TrafficVerification;
}): ReactNode {
  const badge = VERIFICATION_BADGES[verification];
  return (
    <span className={badge.className} title={badge.title}>
      {badge.text}
    </span>
  );
}

export function KeyCell({
  dimension,
  row,
  status,
}: {
  dimension: TrafficTopDimension;
  row: TrafficTopRow;
  status?: AddressLookupRow | undefined;
}): ReactNode {
  const name = dimension === 'bot' ? botLabel(row.key) : row.key;
  return (
    <>
      {dimension === 'ip' ? (
        <AddressLink ip={row.key} />
      ) : dimension === 'path' ? (
        <code>{name}</code>
      ) : (
        name
      )}
      {status !== undefined && (
        <>
          {' '}
          <AddressStatusBadge status={status.status} />
        </>
      )}
      {row.verification !== undefined && (
        <>
          {' '}
          <VerificationBadge verification={row.verification} />
        </>
      )}
      {row.botCategory !== undefined && (
        <span className="cell-sub">{CATEGORY_LABELS[row.botCategory]}</span>
      )}
      {row.geo !== undefined && (
        <span className="cell-sub">
          <Flag code={row.geo.country} />
          {row.geo.network !== null && ` ${row.geo.network}`}
        </span>
      )}
    </>
  );
}

/** Whether the rules in force reach this bot, and the buttons its server can honour. */
export function BotActions({ botKey, actor }: { botKey: string; actor: Actor }): ReactNode {
  // A `fake:` row is the impostors: an IP ban is what stops them, not a name.
  const bot = botKey.startsWith('fake:') ? undefined : botById(BOT_CATALOG, botKey);
  if (bot === undefined) return null;
  const { effective, capabilities } = actor;
  const allowed = effective.allowed_bots?.includes(bot.id) === true;
  const blocked = !allowed && effective.blocked_bots?.includes(bot.id) === true;
  const limited =
    !allowed &&
    (effective.rate_limits ?? []).some((rule) => {
      const target = rateLimitTarget(rule.match);
      return target.kind === 'bot' ? target.id === bot.id : target.category === bot.category;
    });
  return (
    <div className="row-actions">
      {allowed && <span className="badge badge-muted">allowed</span>}
      {blocked && <span className="badge badge-muted">blocked</span>}
      {limited && <span className="badge badge-muted">limited</span>}
      {capabilities?.uaBlock === true && !blocked && !allowed && (
        <Button
          variant="ghost"
          aria-label={`Block ${bot.name}`}
          onClick={() => {
            actor.onBot(bot, 'block');
          }}
        >
          Block
        </Button>
      )}
      {capabilities !== undefined && capabilities.rateLimit !== 'none' && !allowed && (
        <Button
          variant="ghost"
          aria-label={`Rate-limit ${bot.name}`}
          onClick={() => {
            actor.onBot(bot, 'rate_limit');
          }}
        >
          Rate-limit
        </Button>
      )}
      {capabilities !== undefined && !allowed && (
        <Button
          variant="ghost"
          aria-label={`Allow ${bot.name}`}
          onClick={() => {
            actor.onBot(bot, 'allow');
          }}
        >
          Allow
        </Button>
      )}
    </div>
  );
}

/** A ban reaches the website only where the server can enforce it for visitors. */
export function BanAction({ ip, actor }: { ip: string; actor: Actor }): ReactNode {
  const ban = actor.capabilities?.webIpBan;
  if (ban === undefined || ban === 'none') return null;
  return (
    <Button
      variant="ghost"
      aria-label={`Ban ${ip}`}
      onClick={() => {
        actor.onBan(ip);
      }}
    >
      Ban
    </Button>
  );
}
